import { getHttpAuthHeader, http } from "./http";
import {
  Product,
  ProductCategory,
  ProductCreateOrUpdatePayload,
  ProductListResponse,
} from "../types/products";

function toFormData(
  payload: ProductCreateOrUpdatePayload
): FormData | undefined {
  const fd = new FormData();
  let hasFile = false;
  let hasFlags = false;

  if (payload.model3dFile) {
    fd.append("model3d", payload.model3dFile);
    hasFile = true;
  }
  if (payload.clearModel3d !== undefined) {
    fd.set("clearModel3d", String(payload.clearModel3d));
    hasFlags = true;
  }
  if (payload.model3dEngravingMaterial !== undefined)
    fd.set("model3dEngravingMaterial", payload.model3dEngravingMaterial);
  if (payload.model3dPhotoMaterial !== undefined)
    fd.set("model3dPhotoMaterial", payload.model3dPhotoMaterial);
  if (payload.model3dPhotoMaterial !== undefined || payload.model3dWidthMm !== undefined)
    fd.set("model3dWidthMm", payload.model3dWidthMm == null ? "" : String(payload.model3dWidthMm));
  fd.set("category", payload.category);
  fd.set("name", payload.name);
  fd.set("slug", payload.slug);
  fd.set("price", String(payload.price));
  fd.set("stockCount", String(payload.stockCount ?? 0));
  fd.set("isActive", String(payload.isActive ?? true));
  if ((payload as any).isCustomizable != null) {
    fd.set("isCustomizable", String((payload as any).isCustomizable));
  }
  if ((payload as any).giftWrapEnabled != null) {
    fd.set("giftWrapEnabled", String((payload as any).giftWrapEnabled));
  }
  if (payload.description) fd.set("description", payload.description);
  if (payload.jewelryCareHtml != null)
    fd.set("jewelryCareHtml", payload.jewelryCareHtml);
  fd.set("seoTitle", payload.seoTitle ?? "");
  fd.set("seoDescription", payload.seoDescription ?? "");
  fd.set("seoSlug", payload.seoSlug ?? "");
  if (payload.compareAtPrice != null)
    fd.set("compareAtPrice", String(payload.compareAtPrice));
  if (payload.offerPercent != null)
    fd.set("offerPercent", String(payload.offerPercent));
  if (payload.featured != null) fd.set("featured", String(payload.featured));
  if ((payload as any).topSeller != null)
    fd.set("topSeller", String((payload as any).topSeller));
  if (payload.sku) fd.set("sku", payload.sku);
  if (payload.color) fd.set("color", payload.color);
  if (payload.material) fd.set("material", payload.material);
  if (payload.tags?.length) fd.set("tags", JSON.stringify(payload.tags));
  if (payload.seoKeywords !== undefined) {
    fd.set("seoKeywords", JSON.stringify(payload.seoKeywords ?? []));
  }
  if (payload.dimensions)
    fd.set("dimensions", JSON.stringify(payload.dimensions));
  fd.set("images", JSON.stringify(payload.images ?? []));
  if ((payload as any).youtubeVideoUrl !== undefined) {
    fd.set("youtubeVideoUrl", String((payload as any).youtubeVideoUrl || ""));
    hasFlags = true;
  }
  if ((payload as any).useYoutubeAsThumbnail != null) {
    fd.set(
      "useYoutubeAsThumbnail",
      String(Boolean((payload as any).useYoutubeAsThumbnail)),
    );
    hasFlags = true;
  }
  if ((payload as any).virtualTryOn !== undefined) {
    fd.set("virtualTryOn", JSON.stringify((payload as any).virtualTryOn));
    hasFlags = true;
  }
  if ((payload as any).clearPreviewImage != null)
    fd.set("clearPreviewImage", String((payload as any).clearPreviewImage));
  if ((payload as any).clearArImage != null)
    fd.set("clearArImage", String((payload as any).clearArImage));
  if ((payload as any).clearBackImage != null)
    fd.set("clearBackImage", String((payload as any).clearBackImage));
  if ((payload as any).removeMoreImages?.length)
    fd.set(
      "removeMoreImages",
      JSON.stringify((payload as any).removeMoreImages)
    );

  switch (payload.category) {
    case ProductCategory.Pendant:
      if (payload.chainOptions !== undefined) {
        const chainOptions = (payload.chainOptions || []).map((option: any) => ({
          chainType: option.chainType,
          lengthsInch: Array.isArray(option.lengthsInch) ? option.lengthsInch : [],
          colors: Array.isArray(option.colors) ? option.colors : [],
          image: option.image,
          defaultIncluded: option.defaultIncluded,
          upgradePrice: option.upgradePrice,
          standalonePrice: option.standalonePrice,
        }));
        fd.set("chainOptions", JSON.stringify(chainOptions));
      }
      if (payload.availableColors !== undefined) {
        fd.set("availableColors", JSON.stringify(payload.availableColors));
      }
      if (payload.colorStocks !== undefined) {
        fd.set("colorStocks", JSON.stringify(payload.colorStocks));
      }
      if ((payload as any).colorPrices !== undefined) {
        fd.set("colorPrices", JSON.stringify((payload as any).colorPrices));
      }
      if ((payload as any).pendantColorVariants !== undefined) {
        const variants = ((payload as any).pendantColorVariants || []).map(
          (variant: any) => ({
            color: variant.color,
            frontImage: variant.frontImage,
            backImage: variant.backImage,
            moreImages: Array.isArray(variant.moreImages)
              ? variant.moreImages
              : [],
            photoCustomization: variant.photoCustomization,
            backPhotoCustomization: variant.backPhotoCustomization,
          })
        );
        fd.set("pendantColorVariants", JSON.stringify(variants));
      }
      if ((payload as any).backImage)
        fd.set("backImage", String((payload as any).backImage));
      if (payload.chainType) fd.set("chainType", payload.chainType);
      if (payload.chainLengthInch?.length) {
        fd.set("chainLengthInch", JSON.stringify(payload.chainLengthInch));
      }
      if (payload.photoUploadAllowed != null)
        fd.set("photoUploadAllowed", String(payload.photoUploadAllowed));
      if ((payload as any).isDoubleSidedPendant != null)
        fd.set("isDoubleSidedPendant", String((payload as any).isDoubleSidedPendant));
      if (payload.photoCustomization)
        fd.set("photoCustomization", JSON.stringify(payload.photoCustomization));
      if ((payload as any).backPhotoCustomization)
        fd.set(
          "backPhotoCustomization",
          JSON.stringify((payload as any).backPhotoCustomization)
        );
      if (payload.engraving)
        fd.set("engraving", JSON.stringify(payload.engraving));
      break;
    case ProductCategory.Engraving:
      if (payload.engravingSubtype)
        fd.set("engravingSubtype", payload.engravingSubtype);
      if (payload.baseItem) fd.set("baseItem", payload.baseItem);
      if (payload.availableColors !== undefined)
        fd.set("availableColors", JSON.stringify(payload.availableColors));
      if (payload.colorStocks !== undefined)
        fd.set("colorStocks", JSON.stringify(payload.colorStocks));
      if (payload.colorPrices !== undefined)
        fd.set("colorPrices", JSON.stringify(payload.colorPrices));
      if (payload.engravingColorVariants !== undefined) {
        const variants = payload.engravingColorVariants.map((variant) => ({
          color: variant.color,
          sku: variant.sku,
          stockCount: variant.stockCount,
          price: variant.price,
          swatchHex: variant.swatchHex,
          frontImage: variant.frontImage,
          backImage: variant.backImage,
          moreImages: Array.isArray(variant.moreImages)
            ? variant.moreImages
            : [],
          matchingChain: variant.matchingChain,
        }));
        fd.set("engravingColorVariants", JSON.stringify(variants));
      }
      if (payload.defaultChainByColor !== undefined)
        fd.set(
          "defaultChainByColor",
          JSON.stringify(payload.defaultChainByColor)
        );
      if (payload.frontEngravingArea)
        fd.set(
          "frontEngravingArea",
          JSON.stringify(payload.frontEngravingArea)
        );
      if (payload.backEngravingArea)
        fd.set("backEngravingArea", JSON.stringify(payload.backEngravingArea));
      if (payload.engravingAreas !== undefined)
        fd.set("engravingAreas", JSON.stringify(payload.engravingAreas));
      if (payload.supportsBackEngraving != null)
        fd.set(
          "supportsBackEngraving",
          String(payload.supportsBackEngraving)
        );
      if (payload.backEngravingExtraCost != null)
        fd.set(
          "backEngravingExtraCost",
          String(payload.backEngravingExtraCost)
        );
      if (payload.frontTextLimit != null)
        fd.set("frontTextLimit", String(payload.frontTextLimit));
      if (payload.backTextLimit != null)
        fd.set("backTextLimit", String(payload.backTextLimit));
      if (payload.frontMaxLines != null)
        fd.set("frontMaxLines", String(payload.frontMaxLines));
      if (payload.backMaxLines != null)
        fd.set("backMaxLines", String(payload.backMaxLines));
      if (payload.allowedFonts !== undefined)
        fd.set("allowedFonts", JSON.stringify(payload.allowedFonts));
      if (payload.defaultFont) fd.set("defaultFont", payload.defaultFont);
      if (payload.defaultFontSize != null)
        fd.set("defaultFontSize", String(payload.defaultFontSize));
      if (payload.allowedSymbols !== undefined)
        fd.set("allowedSymbols", JSON.stringify(payload.allowedSymbols));
      if (payload.emojiSupportMode)
        fd.set("emojiSupportMode", payload.emojiSupportMode);
      if ((payload as any).backImage)
        fd.set("backImage", String((payload as any).backImage));
      break;
    case ProductCategory.Cup:
      if (payload.size) fd.set("size", payload.size);
      if (payload.photoUploadAllowed != null)
        fd.set("photoUploadAllowed", String(payload.photoUploadAllowed));
      break;
    case ProductCategory.LicensePlate:
      if (payload.plateType) fd.set("plateType", payload.plateType);
      break;
    case ProductCategory.Wallet:
      if (payload.slots != null) fd.set("slots", String(payload.slots));
      if (payload.hasCoinPocket != null)
        fd.set("hasCoinPocket", String(payload.hasCoinPocket));
      if (payload.photoEmbedAllowed != null)
        fd.set("photoEmbedAllowed", String(payload.photoEmbedAllowed));
      break;
    case ProductCategory.PhoneCover:
      if (payload.phoneModel) fd.set("phoneModel", payload.phoneModel);
      if (payload.caseSize) fd.set("caseSize", payload.caseSize);
      if (payload.cameraCutout)
        fd.set("cameraCutout", JSON.stringify(payload.cameraCutout));
      if (payload.finish) fd.set("finish", payload.finish);
      if (payload.photoUploadAllowed != null)
        fd.set("photoUploadAllowed", String(payload.photoUploadAllowed));
      break;
    case ProductCategory.Tshirt:
      if (payload.size) fd.set("size", payload.size);
      if (payload.tshirtType) fd.set("tshirtType", payload.tshirtType);
      if ((payload as any).backCustomizationExtraCost != null) {
        fd.set(
          "backCustomizationExtraCost",
          String((payload as any).backCustomizationExtraCost)
        );
      }
      if (payload.availableColors !== undefined)
        fd.set("availableColors", JSON.stringify(payload.availableColors));
      if (payload.sizeStockByColor !== undefined)
        fd.set("sizeStockByColor", JSON.stringify(payload.sizeStockByColor));
      if (payload.colorStocks !== undefined)
        fd.set("colorStocks", JSON.stringify(payload.colorStocks));
      if (payload.availableSizes !== undefined)
        fd.set("availableSizes", JSON.stringify(payload.availableSizes));
      if (payload.sizeStocks !== undefined)
        fd.set("sizeStocks", JSON.stringify(payload.sizeStocks));
      if (payload.colorVariants !== undefined) {
        const variants = payload.colorVariants.map((variant) => ({
          color: variant.color,
          frontImage: variant.frontImage,
          backImage: variant.backImage,
          moreImages: Array.isArray(variant.moreImages)
            ? variant.moreImages
            : [],
        }));
        fd.set("colorVariants", JSON.stringify(variants));
      }
      if (payload.photoUploadAllowed != null)
        fd.set("photoUploadAllowed", String(payload.photoUploadAllowed));
      break;
    case ProductCategory.Gift:
      if ((payload as any).giftType) {
        fd.set("giftType", String((payload as any).giftType));
      }
      break;
  }

  if ((payload as any).heroImageFile) {
    fd.append("heroImage", (payload as any).heroImageFile);
    hasFile = true;
  }
  if (payload.previewImageFile) {
    fd.append("previewImage", payload.previewImageFile);
    hasFile = true;
  }
  if (payload.arImageFile) {
    fd.append("arImage", payload.arImageFile);
    hasFile = true;
  }
  if ((payload as any).virtualTryOnAssetFile) {
    fd.append("virtualTryOnAsset", (payload as any).virtualTryOnAssetFile);
    hasFile = true;
  }
  if ((payload as any).backImageFile) {
    fd.append("backImage", (payload as any).backImageFile);
    hasFile = true;
  }
  if (payload.moreImageFiles?.length) {
    payload.moreImageFiles.forEach((f) => fd.append("moreImages", f));
    hasFile = true;
  }
  if (
    payload.category === ProductCategory.Pendant &&
    Array.isArray((payload as any).pendantColorVariants) &&
    (payload as any).pendantColorVariants.length
  ) {
    (payload as any).pendantColorVariants.forEach(
      (variant: any, index: number) => {
        if (variant.frontImageFile) {
          fd.append(`pendantColorVariantFrontImage_${index}`, variant.frontImageFile);
          hasFile = true;
        }
        if (!(payload as any).isDoubleSidedPendant && variant.backImageFile) {
          fd.append(`pendantColorVariantBackImage_${index}`, variant.backImageFile);
          hasFile = true;
        }
        if (Array.isArray(variant.moreImageFiles) && variant.moreImageFiles.length) {
          variant.moreImageFiles.forEach((file: File) => {
            fd.append(`pendantColorVariantMoreImages_${index}`, file);
            hasFile = true;
          });
        }
      }
    );
  }
  if (
    payload.category === ProductCategory.Engraving &&
    Array.isArray(payload.engravingColorVariants) &&
    payload.engravingColorVariants.length
  ) {
    payload.engravingColorVariants.forEach((variant, index) => {
      if (variant.frontImageFile) {
        fd.append(
          `engravingColorVariantFrontImage_${index}`,
          variant.frontImageFile
        );
        hasFile = true;
      }
      if (variant.backImageFile) {
        fd.append(
          `engravingColorVariantBackImage_${index}`,
          variant.backImageFile
        );
        hasFile = true;
      }
      if (Array.isArray(variant.moreImageFiles) && variant.moreImageFiles.length) {
        variant.moreImageFiles.forEach((file) => {
          fd.append(`engravingColorVariantMoreImages_${index}`, file);
          hasFile = true;
        });
      }
    });
  }
  if (payload.category === ProductCategory.Pendant && Array.isArray(payload.chainOptions)) {
    payload.chainOptions.forEach((option: any, index: number) => {
      if (option?.imageFile) {
        fd.append(`chainOptionImage_${index}`, option.imageFile);
        hasFile = true;
      }
    });
  }
  if (payload.category === ProductCategory.Tshirt && payload.colorVariants?.length) {
    payload.colorVariants.forEach((variant, index) => {
      if (variant.frontImageFile) {
        fd.append(`tshirtColorVariantFrontImage_${index}`, variant.frontImageFile);
        hasFile = true;
      }
      if (variant.backImageFile) {
        fd.append(`tshirtColorVariantBackImage_${index}`, variant.backImageFile);
        hasFile = true;
      }
      if (Array.isArray(variant.moreImageFiles) && variant.moreImageFiles.length) {
        variant.moreImageFiles.forEach((file) => {
          fd.append(`tshirtColorVariantMoreImages_${index}`, file);
          hasFile = true;
        });
      }
    });
  }

  if ((payload as any).clearHeroImage != null) {
    fd.set("clearHeroImage", String((payload as any).clearHeroImage));
    hasFlags = true;
  }
  if ((payload as any).clearPreviewImage != null) {
    fd.set("clearPreviewImage", String((payload as any).clearPreviewImage));
    hasFlags = true;
  }
  if ((payload as any).clearArImage != null) {
    fd.set("clearArImage", String((payload as any).clearArImage));
    hasFlags = true;
  }
  if ((payload as any).clearBackImage != null) {
    fd.set("clearBackImage", String((payload as any).clearBackImage));
    hasFlags = true;
  }
  if ((payload as any).removeMoreImages?.length) {
    fd.set(
      "removeMoreImages",
      JSON.stringify((payload as any).removeMoreImages)
    );
    hasFlags = true;
  }

  // legacy images array to keep BE happy
  fd.set("images", JSON.stringify(payload.images ?? []));

  // ⬅️ Return FormData if we have files OR flags
  return hasFile || hasFlags ? fd : undefined;
}

export const ProductsAPI = {
  async list(params: {
    page?: number;
    limit?: number;
    category?: ProductCategory;
    featured?: boolean;
    q?: string;
    includeInactive?: boolean;
  }) {
    const r = await http.get<ProductListResponse>("/products", {
      params: {
        ...params,
        includeInactive:
          params.includeInactive === undefined ? true : params.includeInactive,
      },
    });
    return r.data;
  },
  async getById(id: string) {
    const r = await http.get<Product>(`/products/${id}`, {
      params: { includeInactive: true },
    });
    return r.data;
  },
  async getBySlug(slug: string) {
    const r = await http.get<Product>(`/products/slug/${slug}`, {
      params: { includeInactive: true },
    });
    return r.data;
  },
  async categories() {
    const r = await http.get<ProductCategory[]>("/products/categories");
    return r.data;
  },
  async create(payload: ProductCreateOrUpdatePayload) {
    const fd = toFormData(payload);
    if (fd) {
      const r = await http.post<Product>("/products", fd, {
        headers: { ...getHttpAuthHeader() },
      });
      return r.data;
    }
    const r_1 = await http.post<Product>("/products", payload);
    return r_1.data;
  },
  async update(id: string, payload: ProductCreateOrUpdatePayload) {
    const fd = toFormData(payload);
    if (fd) {
      const r = await http.put<Product>(`/products/${id}`, fd, {
        headers: { ...getHttpAuthHeader() },
      });
      return r.data;
    }
    const r_1 = await http.put<Product>(`/products/${id}`, payload);
    return r_1.data;
  },
  async patch(id: string, payload: Record<string, unknown>) {
    const r = await http.put<Product>(`/products/${id}`, payload);
    return r.data;
  },
  async remove(id: string) {
    const r = await http.delete<{ message: string }>(`/products/${id}`);
    return r.data;
  },
};
